import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, X, Loader2, ChevronDown, ChevronUp, RefreshCw, Sparkles } from 'lucide-react';
import { apiService } from '../../services/api';

const WEEKDAYS = [
  { value: 'mon', label: 'Lun' },
  { value: 'tue', label: 'Mar' },
  { value: 'wed', label: 'Mer' },
  { value: 'thu', label: 'Jeu' },
  { value: 'fri', label: 'Ven' },
  { value: 'sat', label: 'Sam' },
  { value: 'sun', label: 'Dim' },
];

const defaultAutomationForm = {
  title: '',
  prompt: '',
  triggerType: 'daily',
  time: '09:00',
  days_of_week: ['mon', 'tue', 'wed', 'thu', 'fri'],
  oneShotAt: '',
  timezone: 'Europe/Paris',
  condition: '',
  condition_prompt: '',
  execution_prompt: '',
  stop_condition: '',
  stop_mode: 'auto_on_success',
  notify_user: true,
  notify_on_error: true,
};

const formatTimeValue = (hour, minute) => {
  if (typeof hour !== 'number' || typeof minute !== 'number') return '09:00';
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

const formatDateForInput = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const tzOffset = date.getTimezoneOffset() * 60000;
  const local = new Date(date.getTime() - tzOffset);
  return local.toISOString().slice(0, 16);
};

const summarizeAutomation = (automation) => {
  const metadata = parseAutomationMetadata(automation.metadata);
  if (metadata.trigger_summary) {
    return metadata.trigger_summary;
  }
  if (automation.trigger_type === 'one_shot') {
    return automation.one_shot_at ? `One shot le ${new Date(automation.one_shot_at).toLocaleString()}` : 'One shot';
  }
  if (automation.trigger_type === 'objective_update') {
    const ids = Array.isArray(metadata.objective_ids) && metadata.objective_ids.length
      ? metadata.objective_ids.join(', ')
      : 'objectifs surveillés';
    const statuses = Array.isArray(metadata.objective_statuses) && metadata.objective_statuses.length
      ? metadata.objective_statuses.join(', ')
      : 'tous statuts';
    const event = metadata.objective_event || 'update';
    return `Surveillance d'objectifs ${ids} (événement ${event}, statuts ${statuses}).`;
  }
  const time = formatTimeValue(automation.hour ?? 9, automation.minute ?? 0);
  const days = automation.days_of_week?.length
    ? automation.days_of_week.map((d) => d.slice(0, 3).toUpperCase()).join(', ')
    : 'Jours ouvrés';
  return `Quotidien ${time} (${days})`;
};

const parseAutomationMetadata = (raw) => {
  if (!raw) {
    return {};
  }
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch (err) {
      console.warn('Impossible de parser les métadonnées automation', err);
      return {};
    }
  }
  return raw;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
};

const stopModeLabels = {
  manual: 'Manuel',
  auto_on_success: 'Auto après succès',
  agent: 'Décision agent StopChecker',
};

const formatStopModeLabel = (mode) => stopModeLabels[mode] || mode || 'manual';

const statusStyles = {
  success: 'bg-green-100 text-green-700',
  error: 'bg-red-100 text-red-700',
  skipped: 'bg-amber-100 text-amber-700',
  pending: 'bg-gray-100 text-gray-600',
};

const StatusBadge = ({ status }) => {
  const style = statusStyles[status] || statusStyles.pending;
  const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Inconnu';
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${style}`}>{label}</span>;
};

const AutomationDiagnostics = ({ metadata }) => {
  if (!metadata) {
    return null;
  }
  const rows = [];
  const conditionResult = metadata.condition_result;
  if (conditionResult) {
    const tone = conditionResult.condition_met === false ? 'text-red-600' : conditionResult.condition_met ? 'text-green-700' : 'text-gray-700';
    const label = conditionResult.error
      ? 'ConditionChecker en erreur'
      : conditionResult.condition_met === false
        ? 'Condition non remplie'
        : 'Condition validée';
    const text =
      conditionResult.justification ||
      conditionResult.evidence ||
      conditionResult.error ||
      conditionResult.reason ||
      JSON.stringify(conditionResult);
    rows.push({ key: 'condition', label, text, tone });
  }
  const stopDecision = metadata.stop_decision;
  if (stopDecision) {
    const statusText =
      typeof stopDecision.deactivate === 'boolean'
        ? stopDecision.deactivate
          ? 'désactivation demandée'
          : 'maintien'
        : null;
    const text =
      stopDecision.justification ||
      stopDecision.reason ||
      stopDecision.error ||
      JSON.stringify(stopDecision);
    rows.push({
      key: 'stop',
      label: `Arrêt (${formatStopModeLabel(stopDecision.mode)})${statusText ? ` • ${statusText}` : ''}`,
      text,
      tone: stopDecision.deactivate ? 'text-amber-700' : 'text-gray-700',
    });
  }
  const notifyDecision = metadata.notify_user_decision;
  if (notifyDecision) {
    rows.push({
      key: 'notify',
      label: 'Notification utilisateur',
      text: notifyDecision.should_notify
        ? 'Message transmis à l’utilisateur.'
        : 'Aucun message envoyé (mode silencieux).',
      tone: notifyDecision.should_notify ? 'text-indigo-700' : 'text-slate-600',
    });
  }
  if (metadata.deactivate && !rows.find((row) => row.key === 'stop')) {
    rows.push({
      key: 'deactivate',
      label: 'Désactivation',
      text: "Cette exécution a demandé de désactiver l'automatisation.",
      tone: 'text-amber-700',
    });
  }
  if (metadata.source || metadata.trigger_type) {
    rows.push({
      key: 'source',
      label: 'Contexte run',
      text: `${metadata.source || 'scheduler'} • trigger ${metadata.trigger_type || 'n/a'}`,
    });
  }
  if (!rows.length) {
    return null;
  }
  return (
    <div className="grid gap-2 md:grid-cols-2">
      {rows.map((row) => (
        <div key={row.key} className="rounded-lg border border-gray-100 bg-white/70 p-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{row.label}</p>
          <p className={`text-sm whitespace-pre-wrap text-gray-700 ${row.tone || ''}`}>{row.text}</p>
        </div>
      ))}
    </div>
  );
};

const buildTriggerSummary = (form) => {
  const timezone = form.timezone?.trim() || 'Europe/Paris';
  if (form.triggerType === 'one_shot') {
    if (!form.oneShotAt) {
      return `Exécution unique à planifier (${timezone}).`;
    }
    const date = new Date(form.oneShotAt);
    const formatted = Number.isNaN(date.getTime()) ? form.oneShotAt : date.toLocaleString('fr-FR');
    return `Exécution unique le ${formatted} (${timezone}).`;
  }
  const [rawHour = '09', rawMinute = '00'] = (form.time || '09:00').split(':');
  const hour = rawHour.padStart(2, '0');
  const minute = rawMinute.padStart(2, '0');
  const activeDays = (form.days_of_week?.length ? form.days_of_week : WEEKDAYS.map((d) => d.value)).map((day) =>
    day.slice(0, 3).toUpperCase(),
  );
  return `Exécution quotidienne à ${hour}h${minute} (${timezone}) les jours ${activeDays.join(', ')}.`;
};

const buildAutomationPrompt = ({
  title,
  triggerSummary,
  timezone,
  conditionText,
  executionInstructions,
  stopCondition,
  additionalNotes,
}) => {
  const lines = [
    'Tu es déclenché automatiquement par Nexus Automation.',
    `Titre de l'automatisation : ${title}.`,
    `Trigger : ${triggerSummary} (fuseau ${timezone}).`,
    "Agis pour le compte de l'utilisateur et ne pose aucune question de confirmation.",
    'Utilise les outils MCP nécessaires pour comprendre le contexte avant d’agir.',
    '',
    'SECTION CONDITION :',
    conditionText || "L’agent ConditionChecker fournit dans metadata.condition_result les preuves évaluées. Appuie-toi dessus.",
    'Si la condition n’est pas remplie, explique pourquoi et ce qu’il faut surveiller.',
    '',
    'SECTION EXECUTION (Nexus -> Nexus) :',
    executionInstructions,
    '',
    'SECTION CONDITION D’ARRÊT :',
    stopCondition || 'Arrêt manuel par défaut.',
  ];
  if (additionalNotes) {
    lines.push('');
    lines.push('Instructions supplémentaires fournies par l’utilisateur :');
    lines.push(additionalNotes);
  }
  lines.push('');
  lines.push("Prépare un message clair à transmettre à l'utilisateur à la fin de l'exécution.");
  return lines.join('\n');
};

const buildAutomationMetadata = (form) => {
  const triggerSummary = buildTriggerSummary(form);
  const timezone = form.timezone?.trim() || 'Europe/Paris';
  const metadata = {
    trigger_summary: triggerSummary,
    stop_mode: form.stop_mode || 'manual',
    trigger_type: form.triggerType,
    timezone,
  };
  const notifyUser = form.notify_user !== undefined ? !!form.notify_user : true;
  const notifyOnError = form.notify_on_error !== undefined ? !!form.notify_on_error : true;
  const conditionText = form.condition.trim();
  if (conditionText) {
    metadata.condition = conditionText;
    metadata.condition_text = conditionText;
  }
  const conditionPrompt = form.condition_prompt.trim() || conditionText;
  if (conditionPrompt) {
    metadata.condition_prompt = conditionPrompt;
  }
  const executionInstructions = form.execution_prompt.trim();
  if (executionInstructions) {
    metadata.execution_instructions = executionInstructions;
  }
  const stopCondition = form.stop_condition.trim();
  if (stopCondition) {
    metadata.stop_condition = stopCondition;
  }
  const freePrompt = form.prompt.trim();
  if (freePrompt) {
    metadata.free_prompt = freePrompt;
  }
  metadata.notify_user = notifyUser;
  metadata.notify_on_error = notifyOnError;
  return {
    metadata,
    triggerSummary,
    conditionText,
    executionInstructions,
    stopCondition,
    freePrompt,
    timezone,
    notifyUser,
    notifyOnError,
  };
};

const Card = ({ title, description, actions, children, className = '', ...rest }) => (
  <div
    {...rest}
    className={`relative overflow-hidden rounded-3xl border border-slate-100 bg-white/95 p-6 shadow-lg shadow-slate-200/70 ${className}`}
  >
    <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-indigo-50/40" aria-hidden />
    <div className="relative space-y-4">
      <div className="flex flex-col gap-3 border-b border-slate-100 pb-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          {description && <p className="text-sm text-gray-500">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  </div>
);

const StatCard = ({ label, value, helper }) => (
  <div className="rounded-2xl border border-slate-100 bg-white/80 p-4 shadow-sm shadow-slate-200/60">
    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</p>
    <p className="mt-2 text-2xl font-semibold text-gray-900">{value}</p>
    {helper && <p className="text-xs text-gray-500">{helper}</p>}
  </div>
);

const EmptyState = ({ message }) => (
  <div className="rounded-xl border border-dashed border-gray-200 p-4 text-gray-500 text-sm text-center">
    {message}
  </div>
);

const UserAiSettings = () => {
  const [instructions, setInstructions] = useState([]);
  const [knowledge, setKnowledge] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [instructionInput, setInstructionInput] = useState('');
  const [knowledgeContent, setKnowledgeContent] = useState('');
  const [knowledgeTags, setKnowledgeTags] = useState('');
  const [automationForm, setAutomationForm] = useState(defaultAutomationForm);

  const [editingInstruction, setEditingInstruction] = useState(null); // { id, content }
  const [editingKnowledge, setEditingKnowledge] = useState(null); // { id, content, tags }
  const [editingAutomationId, setEditingAutomationId] = useState(null);
  const [automationSaving, setAutomationSaving] = useState(false);
  const [expandedAutomationId, setExpandedAutomationId] = useState(null);
  const [automationRuns, setAutomationRuns] = useState({});
  const [runsLoading, setRunsLoading] = useState({});
  const [automationMessages, setAutomationMessages] = useState([]);
  const [messageAckLoading, setMessageAckLoading] = useState({});
  const [messagesRefreshing, setMessagesRefreshing] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [refreshingAll, setRefreshingAll] = useState(false);

  useEffect(() => {
    fetchData(true);
  }, []);

  const fetchData = async (showLoader = false) => {
    if (showLoader) {
      setLoading(true);
    } else {
      setRefreshingAll(true);
    }
    setError(null);
    try {
      const [instr, know, autoList, autoMessages] = await Promise.all([
        apiService.getUserInstructions(),
        apiService.getUserKnowledge(),
        apiService.getAutomations(),
        apiService.getAutomationMessages(),
      ]);
      setInstructions(instr || []);
      setKnowledge(know || []);
      setAutomations(autoList || []);
      setAutomationMessages(autoMessages || []);
    } catch (err) {
      setError(err.message || 'Impossible de charger vos données IA.');
    } finally {
      if (showLoader) {
        setLoading(false);
      } else {
        setRefreshingAll(false);
      }
    }
  };

  const handleAddInstruction = async (e) => {
    e.preventDefault();
    if (!instructionInput.trim()) return;
    try {
      const created = await apiService.addUserInstruction({ content: instructionInput.trim() });
      setInstructions((prev) => [created, ...prev].slice(0, 20));
      setInstructionInput('');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSaveInstruction = async () => {
    if (!editingInstruction) return;
    try {
      const updated = await apiService.updateUserInstruction(editingInstruction.id, {
        content: editingInstruction.content.trim(),
      });
      setInstructions((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      setEditingInstruction(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteInstruction = async (id) => {
    if (!window.confirm('Supprimer cette instruction ?')) return;
    try {
      await apiService.deleteUserInstruction(id);
      setInstructions((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddKnowledge = async (e) => {
    e.preventDefault();
    if (!knowledgeContent.trim()) return;
    try {
      const created = await apiService.addUserKnowledge({
        content: knowledgeContent.trim(),
        tags: knowledgeTags.trim() || undefined,
      });
      setKnowledge((prev) => [created, ...prev]);
      setKnowledgeContent('');
      setKnowledgeTags('');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSaveKnowledge = async () => {
    if (!editingKnowledge) return;
    try {
      const updated = await apiService.updateUserKnowledge(editingKnowledge.id, {
        content: editingKnowledge.content.trim(),
        tags: editingKnowledge.tags?.trim() || undefined,
      });
      setKnowledge((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      setEditingKnowledge(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteKnowledge = async (id) => {
    if (!window.confirm('Supprimer cette information ?')) return;
    try {
      await apiService.deleteUserKnowledge(id);
      setKnowledge((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      setError(err.message);
    }
  };

  const resetAutomationForm = () => {
    setAutomationForm(defaultAutomationForm);
    setEditingAutomationId(null);
  };

  const handleAutomationInputChange = (field, value) => {
    setAutomationForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAutomationDayToggle = (day) => {
    setAutomationForm((prev) => {
      const exists = prev.days_of_week.includes(day);
      const nextDays = exists
        ? prev.days_of_week.filter((item) => item !== day)
        : [...prev.days_of_week, day];
      return { ...prev, days_of_week: nextDays };
    });
  };

  const buildAutomationPayload = () => {
    const {
      metadata,
      triggerSummary,
      conditionText,
      executionInstructions,
      stopCondition,
      freePrompt,
      timezone,
    } = buildAutomationMetadata(automationForm);
    const payload = {
      title: automationForm.title.trim(),
      prompt: buildAutomationPrompt({
        title: automationForm.title.trim(),
        triggerSummary,
        timezone,
        conditionText,
        executionInstructions,
        stopCondition,
        additionalNotes: freePrompt,
      }),
      trigger_type: automationForm.triggerType,
      timezone,
      is_active: true,
      metadata,
    };
    if (automationForm.triggerType === 'daily') {
      const [hour = '09', minute = '00'] = (automationForm.time || '09:00').split(':');
      payload.hour = Number(hour);
      payload.minute = Number(minute);
      payload.days_of_week = automationForm.days_of_week;
    } else if (automationForm.oneShotAt) {
      payload.one_shot_at = `${automationForm.oneShotAt}:00`.replace('::', ':');
    }
    return payload;
  };

  const fetchAutomationsOnly = async () => {
    try {
      const list = await apiService.getAutomations();
      setAutomations(list || []);
    } catch (err) {
      setError(err.message);
    }
  };

  const refreshAutomationMessages = async () => {
    setMessagesRefreshing(true);
    try {
      const data = await apiService.getAutomationMessages();
      setAutomationMessages(data || []);
    } catch (err) {
      setError(err.message || "Impossible de rafraîchir les messages d'automatisation.");
    } finally {
      setMessagesRefreshing(false);
    }
  };

  const handleAutomationSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!automationForm.title.trim()) {
      setError('Ajoute un titre à ton automatisation.');
      return;
    }
    if (!automationForm.condition.trim()) {
      setError('Décris précisément la condition à surveiller.');
      return;
    }
    if (!automationForm.execution_prompt.trim()) {
      setError("Décris ce que Nexus doit faire dans la section 'Instructions d’exécution'.");
      return;
    }
    try {
      setAutomationSaving(true);
      const payload = buildAutomationPayload();
      if (editingAutomationId) {
        await apiService.updateAutomation(editingAutomationId, payload);
      } else {
        await apiService.createAutomation(payload);
      }
      await fetchAutomationsOnly();
      resetAutomationForm();
    } catch (err) {
      setError(err.message || "Impossible d'enregistrer l'automatisation.");
    } finally {
      setAutomationSaving(false);
    }
  };

  const handleAutomationEdit = (automation) => {
    if (!automation) return;
    setEditingAutomationId(automation.id);
    const metadata = parseAutomationMetadata(automation.metadata);
    const executionInstructions = metadata.execution_instructions || automation.prompt || '';
    setAutomationForm({
      title: automation.title,
      prompt: metadata.free_prompt || '',
      triggerType: automation.trigger_type,
      time: formatTimeValue(automation.hour ?? 9, automation.minute ?? 0),
      days_of_week: automation.days_of_week && automation.days_of_week.length
        ? automation.days_of_week
        : defaultAutomationForm.days_of_week,
      oneShotAt: formatDateForInput(automation.one_shot_at),
      timezone: automation.timezone || 'Europe/Paris',
      condition: metadata.condition_text || metadata.condition || '',
      condition_prompt: (metadata.condition_prompt_user ?? metadata.condition_prompt) || '',
      execution_prompt: executionInstructions,
      stop_condition: metadata.stop_condition || '',
      stop_mode: metadata.stop_mode || defaultAutomationForm.stop_mode,
      notify_user: metadata.notify_user !== undefined ? !!metadata.notify_user : true,
      notify_on_error: metadata.notify_on_error !== undefined ? !!metadata.notify_on_error : true,
    });
    openBuilderSection();
  };

  const handleAutomationDelete = async (automationId) => {
    if (!window.confirm('Supprimer cette automatisation ?')) return;
    try {
      await apiService.deleteAutomation(automationId);
      setAutomations((prev) => prev.filter((item) => item.id !== automationId));
      if (editingAutomationId === automationId) {
        resetAutomationForm();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAutomationToggle = async (automation) => {
    try {
      await apiService.toggleAutomation(automation.id, !automation.is_active);
      await fetchAutomationsOnly();
    } catch (err) {
      setError(err.message);
    }
  };

  const loadAutomationRuns = async (automationId) => {
    if (runsLoading[automationId]) return;
    setRunsLoading((prev) => ({ ...prev, [automationId]: true }));
    try {
      const runs = await apiService.getAutomationRuns(automationId);
      setAutomationRuns((prev) => ({ ...prev, [automationId]: runs || [] }));
    } catch (err) {
      setError(err.message || "Impossible de charger l'historique de l'automatisation.");
    } finally {
      setRunsLoading((prev) => {
        const next = { ...prev };
        delete next[automationId];
        return next;
      });
    }
  };

  const handleToggleAutomationDetails = (automationId) => {
    setExpandedAutomationId((prev) => {
      const next = prev === automationId ? null : automationId;
      if (next && !automationRuns[next]) {
        loadAutomationRuns(next);
      }
      return next;
    });
  };

  const handleAcknowledgeMessage = async (messageId) => {
    setMessageAckLoading((prev) => ({ ...prev, [messageId]: true }));
    try {
      await apiService.acknowledgeAutomationMessage(messageId);
      setAutomationMessages((prev) => prev.filter((msg) => msg.id !== messageId));
    } catch (err) {
      setError(err.message || "Impossible d'accuser réception du message.");
    } finally {
      setMessageAckLoading((prev) => {
        const next = { ...prev };
        delete next[messageId];
        return next;
      });
    }
  };

  const scrollToSection = (sectionId) => {
    if (typeof document === 'undefined') {
      return;
    }
    const target = document.getElementById(sectionId);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const openBuilderSection = () => {
    setBuilderOpen(true);
    setTimeout(() => scrollToSection('automation-builder'), 50);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 text-indigo-500 animate-spin" />
      </div>
    );
  }

  const activeAutomations = automations.filter((automation) => automation.is_active).length;
  const pendingMessages = automationMessages.length;
  const stats = [
    {
      label: 'Consignes IA',
      value: instructions.length || '0',
      helper: instructions.length ? 'Ton ton personnalisé' : 'Aucune instruction définie',
    },
    {
      label: 'Automatisations actives',
      value: activeAutomations || '0',
      helper: automations.length ? `${automations.length} au total` : 'Crée ta première routine',
    },
    {
      label: 'Infos partagées',
      value: knowledge.length || '0',
      helper: knowledge.length ? 'Contextes disponibles pour Nexus' : "Ajoute des points d'ancrage",
    },
    {
      label: 'Alertes en attente',
      value: pendingMessages || '0',
      helper: pendingMessages ? 'À traiter' : 'Tout est lu',
    },
  ];
  const heroHighlights = [
    'Ton paramétrage reste privé par défaut',
    'Diagnostics détaillés sur chaque automation',
    'Des tags et notes pour orienter Nexus',
  ];
  const upcomingAutomation = automations
    .filter((automation) => automation.next_run_at)
    .sort((a, b) => new Date(a.next_run_at) - new Date(b.next_run_at))[0];
  const automationFormSummary = buildTriggerSummary(automationForm);

  return (
    <div className="space-y-8 py-8">
      <section className="relative overflow-hidden rounded-3xl border border-indigo-100 bg-gradient-to-br from-indigo-600 via-indigo-500 to-sky-500 p-8 text-white shadow-xl">
        <div className="absolute inset-0">
          <div className="absolute -top-10 right-0 h-48 w-48 rounded-full bg-sky-400/40 blur-3xl" />
          <div className="absolute bottom-0 left-0 h-52 w-52 rounded-full bg-indigo-900/40 blur-3xl" />
        </div>
        <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-4">
            <p className="text-xs font-semibold uppercase tracking-[0.4em] text-white/70">Mes préférences IA</p>
            <h1 className="text-3xl font-semibold leading-tight">Ton cockpit IA personnel</h1>
            <p className="text-base text-white/80">
              Ordonne la manière dont Nexus te tutoie, automatise les rappels stratégiques et partage les contextes qui te définissent.
            </p>
            <div className="flex flex-wrap gap-3 text-xs text-white/80">
              {heroHighlights.map((highlight) => (
                <span key={highlight} className="inline-flex items-center gap-2 rounded-full border border-white/30 px-3 py-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-white/80" />
                  {highlight}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 pt-2">
              <button
                type="button"
                onClick={() => scrollToSection('instructions-section')}
                className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-indigo-700 shadow-lg transition hover:-translate-y-0.5"
              >
                <Sparkles className="h-4 w-4 text-indigo-500" />
                Documenter une consigne
              </button>
              <button
                type="button"
                onClick={openBuilderSection}
                className="inline-flex items-center gap-2 rounded-full border border-white/60 px-4 py-2 text-sm font-semibold text-white shadow-lg transition hover:bg-white/10"
              >
                <Plus className="h-4 w-4" />
                Programmer une automation
              </button>
              <button
                type="button"
                onClick={() => fetchData()}
                disabled={refreshingAll}
                className="inline-flex items-center gap-2 rounded-full border border-white/40 px-4 py-2 text-sm font-semibold text-white/90 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {refreshingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                {refreshingAll ? 'Actualisation...' : 'Actualiser les données'}
              </button>
            </div>
          </div>
          <div className="w-full max-w-sm rounded-2xl border border-white/20 bg-white/10 p-5 text-sm shadow-xl">
            {upcomingAutomation ? (
              <>
                <p className="text-xs uppercase tracking-[0.3em] text-white/70">Prochaine exécution</p>
                <p className="mt-2 text-lg font-semibold text-white">{upcomingAutomation.title}</p>
                <p className="text-white/80">{formatDateTime(upcomingAutomation.next_run_at)}</p>
                <p className="mt-3 text-xs text-white/80">{summarizeAutomation(upcomingAutomation)}</p>
              </>
            ) : (
              <>
                <p className="text-lg font-semibold text-white">Aucune automation programmée</p>
                <p className="mt-2 text-white/80">Crée un rappel ou demande à Nexus d'en préparer un pour toi.</p>
              </>
            )}
          </div>
        </div>
      </section>

      <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <StatCard key={stat.label} label={stat.label} value={stat.value} helper={stat.helper} />
        ))}
      </dl>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
        <div className="space-y-6">

<Card
          id="instructions-section"
          className="xl:col-span-1"
          title="Instructions personnalisées"
          description="Ces consignes dictent la manière dont Nexus te parle (ton, vocabulaire, préférences)."
        >
          <form onSubmit={handleAddInstruction} className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              className="flex-1 rounded-xl border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Ex: Utilise toujours 'tu' quand tu t'adresses à moi"
              maxLength={100}
              value={instructionInput}
              onChange={(e) => setInstructionInput(e.target.value)}
            />
            <button
              type="submit"
              className="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-4 py-2 text-white text-sm font-medium shadow hover:bg-indigo-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter
            </button>
          </form>

          <div className="space-y-3">
            {instructions.length === 0 ? (
              <EmptyState message="Aucune instruction enregistrée pour l'instant." />
            ) : (
              instructions.map((item) => (
                <div
                  key={item.id}
                  className="rounded-xl border border-gray-100 bg-gray-50 p-3 flex flex-col gap-2"
                >
                  {editingInstruction?.id === item.id ? (
                    <>
                      <textarea
                        value={editingInstruction.content}
                        maxLength={100}
                        onChange={(e) =>
                          setEditingInstruction((prev) => ({ ...prev, content: e.target.value }))
                        }
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <div className="flex justify-end gap-2 text-sm">
                        <button
                          type="button"
                          className="inline-flex items-center px-3 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-white"
                          onClick={() => setEditingInstruction(null)}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Annuler
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center px-3 py-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500"
                          onClick={handleSaveInstruction}
                        >
                          <Pencil className="h-4 w-4 mr-1" />
                          Enregistrer
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-gray-900">{item.content}</p>
                      <div className="flex justify-end gap-2 text-xs text-gray-500">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-white"
                          onClick={() => setEditingInstruction({ id: item.id, content: item.content })}
                        >
                          <Pencil className="h-4 w-4" />
                          Modifier
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-white text-red-500"
                          onClick={() => handleDeleteInstruction(item.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                          Supprimer
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </Card>



        <Card

          title="Infos que Nexus connaît sur toi"

          description="Ces notes permettent à Nexus d'orienter les autres vers toi (ventes, expertises, rôles)."

        >

          <div className="rounded-2xl border border-indigo-100/60 bg-indigo-50/70 p-4 text-xs text-indigo-900">

            Plus tu fournis de contexte, plus Nexus peut te recommander précisément.

          </div>

          <form onSubmit={handleAddKnowledge} className="space-y-3">

            <textarea

              className="w-full rounded-xl border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"

              placeholder="Ex: J'ai porté la vente du projet Atlas et je gère la relation client."

              maxLength={200}

              value={knowledgeContent}

              onChange={(e) => setKnowledgeContent(e.target.value)}

            />

            <input

              type="text"

              className="w-full rounded-xl border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"

              placeholder="Tags (ex: ventes, electronique)"

              value={knowledgeTags}

              onChange={(e) => setKnowledgeTags(e.target.value)}

            />

            <button

              type="submit"

              className="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-4 py-2 text-white text-sm font-medium shadow hover:bg-indigo-500"

            >

              <Plus className="h-4 w-4 mr-2" />

              Ajouter une information

            </button>

          </form>



          <div className="space-y-3">

            {knowledge.length === 0 ? (

              <EmptyState message="Aucune information enregistrée. Ajoute ce que Nexus doit retenir sur ton rôle." />

            ) : (

              knowledge.map((item) => (

                <div key={item.id} className="rounded-2xl border border-gray-100 bg-white/80 p-3 space-y-2 shadow-sm">

                  {editingKnowledge?.id === item.id ? (

                    <>

                      <textarea

                        className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"

                        maxLength={200}

                        value={editingKnowledge.content}

                        onChange={(e) =>

                          setEditingKnowledge((prev) => ({ ...prev, content: e.target.value }))

                        }

                      />

                      <input

                        type="text"

                        className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"

                        placeholder="Tags"

                        value={editingKnowledge.tags || ''}

                        onChange={(e) =>

                          setEditingKnowledge((prev) => ({ ...prev, tags: e.target.value }))

                        }

                      />

                      <div className="flex justify-end gap-2 text-sm">

                        <button

                          type="button"

                          className="inline-flex items-center px-3 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-white"

                          onClick={() => setEditingKnowledge(null)}

                        >

                          <X className="h-4 w-4 mr-1" />

                          Annuler

                        </button>

                        <button

                          type="button"

                          className="inline-flex items-center px-3 py-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500"

                          onClick={handleSaveKnowledge}

                        >

                          <Pencil className="h-4 w-4 mr-1" />

                          Enregistrer

                        </button>

                      </div>

                    </>

                  ) : (

                    <>

                      <p className="text-sm text-gray-900">{item.content}</p>

                      {item.tags && (

                        <div className="flex flex-wrap gap-1">

                          {item.tags.split(',').map((tag) => (

                            <span

                              key={tag}

                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-700"

                            >

                              {tag.trim()}

                            </span>

                          ))}

                        </div>

                      )}

                      <div className="flex justify-end gap-2 text-xs text-gray-500">

                        <button

                          type="button"

                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-white"

                          onClick={() =>

                            setEditingKnowledge({

                              id: item.id,

                              content: item.content,

                              tags: item.tags || '',

                            })

                          }

                        >

                          <Pencil className="h-4 w-4" />

                          Modifier

                        </button>

                        <button

                          type="button"

                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-white text-red-500"

                          onClick={() => handleDeleteKnowledge(item.id)}

                        >

                          <Trash2 className="h-4 w-4" />

                          Supprimer

                        </button>

                      </div>

                    </>

                  )}

                </div>

              ))

            )}

          </div>

        </Card>

        </div>



        <div className="space-y-6">

        <Card
          title="Automatisations Nexus"
          description="Tu peux ajuster ici les rappels automatiques, vérifier leurs triggers et suivre leurs exécutions."
          actions={
            <button
              type="button"
              onClick={() => {
                setBuilderOpen((prev) => {
                  const next = !prev;
                  if (!prev) {
                    setTimeout(() => scrollToSection('automation-builder'), 50);
                  }
                  return next;
                });
              }}
              className="inline-flex items-center gap-2 rounded-full border border-indigo-200 px-3 py-1 text-xs font-semibold text-indigo-600 hover:bg-indigo-50"
            >
              {builderOpen ? (
                <>
                  <X className="h-3 w-3" /> Fermer le formulaire
                </>
              ) : (
                <>
                  <Plus className="h-3 w-3" /> Nouvelle automation
                </>
              )}
            </button>
          }
        >

          {automations.length ? (

            <div className="space-y-4">

              {automations.map((automation) => {

                const metadata = parseAutomationMetadata(automation.metadata);

                const isExpanded = expandedAutomationId === automation.id;

                const runs = automationRuns[automation.id] || [];

                const canEditAutomation = ['daily', 'one_shot'].includes(automation.trigger_type);

                const triggerSummary = metadata.trigger_summary || summarizeAutomation(automation);

                const timezone = metadata.timezone || automation.timezone || 'Europe/Paris';

                const notifySuccessPref = metadata.notify_user !== undefined ? !!metadata.notify_user : true;

                const notifyErrorPref = metadata.notify_on_error !== undefined ? !!metadata.notify_on_error : true;

                return (

                  <div

                    key={automation.id}

                    className="rounded-3xl border border-slate-100 bg-white/80 p-4 shadow-sm shadow-slate-200/60"

                  >

                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">

                      <div className="space-y-1">

                        <p className="font-semibold text-gray-900">{automation.title}</p>

                        <p className="text-sm text-gray-600">{triggerSummary}</p>

                        <div className="flex flex-wrap gap-3 text-[11px] text-gray-500">

                          {automation.next_run_at && <span>Prochaine exécution : {formatDateTime(automation.next_run_at)}</span>}

                          {automation.last_status && (

                            <span className="inline-flex items-center gap-1">

                              Dernier statut :

                              <StatusBadge status={automation.last_status} />

                              {automation.last_response_excerpt && (

                                <span className="text-gray-600">{automation.last_response_excerpt}</span>

                              )}

                            </span>

                          )}

                          <span className="inline-flex items-center gap-1">

                            Fuseau : <strong className="font-semibold text-gray-800">{timezone}</strong>

                          </span>

                          {!notifySuccessPref && (

                            <span className="inline-flex items-center rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-700">

                              Silencieux (succès)

                            </span>

                          )}

                          {!notifyErrorPref && (

                            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">

                              Pas d'alerte erreur

                            </span>

                          )}

                        </div>

                      </div>

                      <div className="flex flex-wrap gap-2 text-sm">

                        <button

                          type="button"

                          onClick={() => handleAutomationToggle(automation)}

                          className={`rounded-full px-3 py-1 ${automation.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}

                        >

                          {automation.is_active ? 'Actif' : 'Inactif'}

                        </button>

                        <button

                          type="button"

                          onClick={() => canEditAutomation && handleAutomationEdit(automation)}

                          className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 ${

                            canEditAutomation ? 'text-gray-700' : 'text-gray-400 cursor-not-allowed'

                          }`}

                          disabled={!canEditAutomation}

                          title={

                            canEditAutomation

                              ? 'Modifier cette automatisation'

                              : 'Modification désactivée pour ce type de trigger (géré par Nexus).'

                          }

                        >

                          <Pencil className="h-3 w-3" /> Modifier

                        </button>

                        <button

                          type="button"

                          onClick={() => handleAutomationDelete(automation.id)}

                          className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-red-600"

                        >

                          <Trash2 className="h-3 w-3" /> Supprimer

                        </button>

                        <button

                          type="button"

                          onClick={() => handleToggleAutomationDetails(automation.id)}

                          className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-indigo-600"

                        >

                          {isExpanded ? (

                            <>

                              <ChevronUp className="h-4 w-4" /> Masquer

                            </>

                          ) : (

                            <>

                              <ChevronDown className="h-4 w-4" /> Détails

                            </>

                          )}

                        </button>

                      </div>

                    </div>

                    {isExpanded && (

                      <div className="mt-4 space-y-4 rounded-2xl border border-slate-100 bg-white/70 p-4">

                        <div className="grid gap-3 md:grid-cols-2">

                          <div className="rounded-xl bg-slate-50/80 p-3">

                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Déclencheur</p>

                            <p className="text-sm text-gray-800 whitespace-pre-wrap">{triggerSummary}</p>

                            <p className="text-xs text-gray-500 mt-1">Fuseau : {timezone}</p>

                          </div>

                          <div className="rounded-xl bg-slate-50/80 p-3">

                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Condition</p>

                            <p className="text-sm text-gray-800 whitespace-pre-wrap">

                              {metadata.condition_text || metadata.condition || 'Non définie'}

                            </p>

                            {metadata.condition_prompt && (

                              <p className="mt-1 text-xs text-gray-500 whitespace-pre-wrap">

                                Agent ConditionChecker : {metadata.condition_prompt}

                              </p>

                            )}

                          </div>

                          <div className="rounded-xl bg-slate-50/80 p-3">

                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Exécution</p>

                            <p className="text-sm text-gray-800 whitespace-pre-wrap">

                              {metadata.execution_instructions || automation.prompt || 'Non renseigné'}

                            </p>

                          </div>

                          <div className="rounded-xl bg-slate-50/80 p-3">

                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Arrêt</p>

                            <p className="text-sm text-gray-800 whitespace-pre-wrap">

                              {metadata.stop_condition || 'Arrêt manuel'}

                            </p>

                            <p className="text-xs text-gray-500 mt-1">Mode : {formatStopModeLabel(metadata.stop_mode || 'manual')}</p>

                          </div>

                        </div>

                        <div>

                          <div className="mb-2 flex items-center justify-between">

                            <h4 className="text-sm font-semibold text-gray-900">Historique des exécutions</h4>

                            <button

                              type="button"

                              onClick={() => loadAutomationRuns(automation.id)}

                              className="inline-flex items-center gap-2 text-xs text-indigo-600 hover:text-indigo-500"

                            >

                              <RefreshCw className={`h-4 w-4 ${runsLoading[automation.id] ? 'animate-spin' : ''}`} />

                              Rafraîchir

                            </button>

                          </div>

                          {runsLoading[automation.id] ? (

                            <div className="flex items-center gap-2 text-sm text-gray-500">

                              <Loader2 className="h-4 w-4 animate-spin" />

                              Chargement de l'historique...

                            </div>

                          ) : runs.length ? (

                            <div className="space-y-3">

                              {runs.map((run) => (

                                <div key={run.id} className="rounded-2xl border border-gray-100 bg-white p-3 space-y-2">

                                  <div className="flex items-center justify-between">

                                    <StatusBadge status={run.status} />

                                    <span className="text-xs text-gray-500">{formatDateTime(run.triggered_at)}</span>

                                  </div>

                                  {run.response_message && (

                                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{run.response_message}</p>

                                  )}

                                  {run.error_message && (

                                    <p className="text-sm text-red-600 whitespace-pre-wrap">{run.error_message}</p>

                                  )}

                                  <AutomationDiagnostics metadata={run.metadata} />

                                </div>

                              ))}

                            </div>

                          ) : (

                            <EmptyState message="Aucune exécution enregistrée pour l'instant." />

                          )}

                        </div>

                      </div>

                    )}

                  </div>

                );

              })}

            </div>

          ) : (

            <EmptyState message="Pas encore d'automatisation créée. Demande à Nexus d'en préparer une ou ajoute-la ici." />

          )}



          <div id="automation-builder">
            {builderOpen && (
              <div className="rounded-3xl border border-slate-100 bg-slate-50/60 p-4 space-y-5">

              <div className="flex flex-col gap-1">

                <p className="text-sm font-semibold text-gray-900">

                  {editingAutomationId ? 'Modifier une automatisation' : 'Composer une automatisation'}

                </p>

                <p className="text-xs text-gray-500">Définis le trigger, les instructions d'exécution et les notifications.</p>

              </div>

              <form onSubmit={handleAutomationSubmit} className="space-y-4">

                <div className="grid gap-4 md:grid-cols-2">

                  <div>

                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">Titre</label>

                    <input

                      type="text"

                      className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"

                      placeholder="Titre"

                      value={automationForm.title}

                      onChange={(e) => handleAutomationInputChange('title', e.target.value)}

                      required

                    />

                  </div>

                  <div>

                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">Fuseau horaire (IANA)</label>

                    <input

                      type="text"

                      className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"

                      value={automationForm.timezone}

                      onChange={(e) => handleAutomationInputChange('timezone', e.target.value)}

                    />

                  </div>

                </div>

                <div className="grid gap-4 md:grid-cols-2">

                  <div>

                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">Type de déclencheur</label>

                    <select

                      className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2"

                      value={automationForm.triggerType}

                      onChange={(e) => handleAutomationInputChange('triggerType', e.target.value)}

                    >

                      <option value="daily">Quotidien</option>

                      <option value="one_shot">One shot</option>

                    </select>

                  </div>

                  <div>

                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">

                      {automationForm.triggerType === 'daily' ? 'Heure locale' : 'Date / heure de déclenchement'}

                    </label>

                    {automationForm.triggerType === 'daily' ? (

                      <input

                        type="time"

                        className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2"

                        value={automationForm.time}

                        onChange={(e) => handleAutomationInputChange('time', e.target.value)}

                      />

                    ) : (

                      <input

                        type="datetime-local"

                        className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2"

                        value={automationForm.oneShotAt}

                        onChange={(e) => handleAutomationInputChange('oneShotAt', e.target.value)}

                        required

                      />

                  )}

                </div>

              </div>

              {automationForm.triggerType === 'daily' && (

                <div>

                  <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">Jours actifs</label>

                  <div className="mt-2 flex flex-wrap gap-2">

                    {WEEKDAYS.map((day) => (

                      <label key={day.value} className="inline-flex items-center rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-gray-700">

                        <input

                          type="checkbox"

                          className="mr-2"

                          checked={automationForm.days_of_week.includes(day.value)}

                          onChange={() => handleAutomationDayToggle(day.value)}

                        />

                        {day.label}

                      </label>

                    ))}

                  </div>

                </div>

              )}

              <div className="grid gap-4 md:grid-cols-2">

                <div>

                  <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">Condition à surveiller *</label>

                  <textarea

                    className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"

                    placeholder="Inclue les IDs, statuts, contexte précis..."

                    rows={3}

                    value={automationForm.condition}

                    onChange={(e) => handleAutomationInputChange('condition', e.target.value)}
                    required

                  />

                  <p className="text-xs text-gray-500 mt-1">Tout ce qui est nécessaire pour décider si l'automatisation doit se déclencher.</p>

                </div>

                <div>

                  <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">Instructions ConditionChecker</label>

                  <textarea

                    className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"

                    placeholder="Explique quels outils/ID utiliser pour vérifier la condition."

                    rows={3}

                    value={automationForm.condition_prompt}

                    onChange={(e) => handleAutomationInputChange('condition_prompt', e.target.value)}

                  />

                </div>

              </div>

              <div className="grid gap-4 md:grid-cols-2">

                <div>

                  <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">Instructions d'exécution *</label>

                  <textarea

                    className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"

                    placeholder="Décris ce que Nexus doit faire si la condition est vraie."

                    rows={3}

                    value={automationForm.execution_prompt}

                    onChange={(e) => handleAutomationInputChange('execution_prompt', e.target.value)}

                    required

                  />

                </div>

                <div>

                  <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">Condition d'arrêt</label>

                  <textarea

                    className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"

                    placeholder="Quand désactiver cette automatisation ?"

                    rows={3}

                    value={automationForm.stop_condition}

                    onChange={(e) => handleAutomationInputChange('stop_condition', e.target.value)}

                  />

                </div>

              </div>

              <div>

                <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">Mode de désactivation</label>

                <select

                  className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2"

                  value={automationForm.stop_mode}

                  onChange={(e) => handleAutomationInputChange('stop_mode', e.target.value)}

                >

                  <option value="manual">Manuel</option>

                  <option value="auto_on_success">Auto après succès</option>

                  <option value="agent">Décision agent StopChecker</option>

                </select>

              </div>

              <div className="grid gap-4 md:grid-cols-2">

                <label className="flex items-start gap-2 rounded-2xl border border-gray-200 bg-white/80 p-3 text-sm text-gray-700">

                  <input

                    type="checkbox"

                    className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"

                    checked={!!automationForm.notify_user}

                    onChange={(e) => handleAutomationInputChange('notify_user', e.target.checked)}

                  />

                  <span>

                    <span className="font-semibold text-gray-900">Informer sur les succès</span>

                    <span className="block text-xs text-gray-500">Décoche pour garder l'automatisation silencieuse après succès.</span>

                  </span>

                </label>

                <label className="flex items-start gap-2 rounded-2xl border border-gray-200 bg-white/80 p-3 text-sm text-gray-700">

                  <input

                    type="checkbox"

                    className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"

                    checked={!!automationForm.notify_on_error}

                    onChange={(e) => handleAutomationInputChange('notify_on_error', e.target.checked)}

                  />

                  <span>

                    <span className="font-semibold text-gray-900">Préviens-moi si une erreur survient</span>

                    <span className="block text-xs text-gray-500">Décoche uniquement si tu acceptes de ne pas être alerté en cas d'échec.</span>

                  </span>

                </label>

              </div>

              <div className="grid gap-4 md:grid-cols-2">

                <div>

                  <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">Notes additionnelles</label>

                  <textarea

                    className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"

                    placeholder="Contexte ou message spécifique pour Nexus."

                    rows={2}

                    value={automationForm.prompt}

                    onChange={(e) => handleAutomationInputChange('prompt', e.target.value)}

                  />

                </div>

                <div className="rounded-2xl border border-gray-100 bg-white p-3 text-sm text-gray-700">

                  <p className="font-semibold text-gray-900">Résumé estimé</p>

                  <p className="text-xs text-gray-500">{automationFormSummary}</p>

                </div>

              </div>

              <div className="flex items-center gap-2">
                <button

                  type="submit"

                  className="flex-1 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"

                  disabled={automationSaving}

                >

                  {automationSaving ? 'Enregistrement...' : editingAutomationId ? 'Mettre à jour' : 'Créer'}

                </button>

                {editingAutomationId && (

                  <button

                    type="button"

                    className="rounded-xl border px-4 py-2 text-sm"

                    onClick={resetAutomationForm}

                  >

                    Annuler

                  </button>

                )}

              </div>

            </form>

              </div>
            )}
          </div>

        </Card>



        <Card

          title="Alertes & diagnostics"

          description="Les derniers messages générés par tes automatisations."

          actions={

            <button

              type="button"

              onClick={refreshAutomationMessages}

              className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1 text-xs text-indigo-600 hover:text-indigo-500"

            >

              <RefreshCw className={`h-4 w-4 ${messagesRefreshing ? 'animate-spin' : ''}`} />

              Actualiser

            </button>

          }

        >

          {automationMessages.length ? (

            <div className="space-y-3">

              {automationMessages.map((message) => (

                <div key={message.id} className="rounded-2xl border border-gray-100 bg-white/80 p-4 space-y-2">

                  <div className="flex items-center justify-between">

                    <div>

                      <p className="font-medium text-gray-900">{message.automation_title}</p>

                      <p className="text-xs text-gray-500">{formatDateTime(message.triggered_at)}</p>

                    </div>

                    <StatusBadge status={message.status} />

                  </div>

                  {message.response_message && (

                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{message.response_message}</p>

                  )}

                  {message.error_message && (

                    <p className="text-sm text-red-600 whitespace-pre-wrap">{message.error_message}</p>

                  )}

                  <AutomationDiagnostics metadata={message.metadata} />

                  <div className="flex justify-end">

                    <button

                      type="button"

                      className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-700 hover:text-indigo-600"

                      onClick={() => handleAcknowledgeMessage(message.id)}

                      disabled={!!messageAckLoading[message.id]}

                    >

                      {messageAckLoading[message.id] ? (

                        <>

                          <Loader2 className="h-3 w-3 animate-spin" /> Traitement...

                        </>

                      ) : (

                        'Marquer comme lu'

                      )}

                    </button>

                  </div>

                </div>

              ))}

            </div>

          ) : (

            <EmptyState message="Aucun message récent. Lance une automatisation pour visualiser ici le résultat et les diagnostics." />

          )}

        </Card>

        </div>

      </div>
    </div>
  );
};

export default UserAiSettings;
